import React, { useCallback, useEffect, useRef, useState } from "react"
import type { Postava } from "./postavy-data"
import nerdFoto from "./nerd-foto-web.png"
import josefFoto from "./josef-foto-web.png"
import karelFoto from "./karel-foto-web.png"
import fandaFoto from "./fanda-foto-web.png"
import zavislakFoto from "./zavislak-foto-web.png"
import zbysekFoto from "./zbysek-foto-web.png"
import favicon from "./favicon.png"
import lockIcon from "./zamek-ikonka.png"

import redbullIcon from "./redbull-item.png"
import monsterIcon from "./monster-item.png"
import antiIcon from "./anti-kofein-item.png"
import kontrolerIcon from "./kontroler-item.png"
import kavaIcon from "./kava-item.png"
import bgMenu from "./bg-menu.png"

import kofeinIkonka from "./kofein-ikonka.png"

type Props = { onZpet: () => void; onNovaHra?: () => void; postava: Postava; jmeno?: string }

export function Hra({ onZpet, postava, jmeno }: Props) {
  const [cookies, setCookies] = useState<number>(0)
  const [logicalCaffeine, setLogicalCaffeine] = useState<number>(0)
  const [displayCaffeine, setDisplayCaffeine] = useState<number>(0)
  const [coffeeCount, setCoffeeCount] = useState<number>(0)
  const [redbullCount, setRedbullCount] = useState<number>(0)
  const [monsterCount, setMonsterCount] = useState<number>(0)
  const [controllerCount, setControllerCount] = useState<number>(0)
  const [tabletCount, setTabletCount] = useState<number>(0)
  const [time, setTime] = useState<number>(0)
  const [gameOver, setGameOver] = useState<boolean>(false)
  const [gameOverReason, setGameOverReason] = useState<"overdose"|"sleep"|null>(null)
  const [bump, setBump] = useState<boolean>(false)
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const [totalCollected, setTotalCollected] = useState<number>(0)
  const [unlockedRed, setUnlockedRed] = useState<boolean>(false)
  const [unlockedMonster, setUnlockedMonster] = useState<boolean>(false)
  const [hasStarted, setHasStarted] = useState<boolean>(false)

  const lastClickRef = useRef<number>(Date.now())
  const imgRef = useRef<HTMLImageElement|null>(null)
  const offscreenCanvasRef = useRef<HTMLCanvasElement|null>(null)
  const caffeineMaskRef = useRef<HTMLDivElement|null>(null)
  const gameOverRef = useRef<boolean>(false)

  const caffeineLimit = postava.kofein_limit
  const caffeineDecayPerSec = 5

  const onImageLoad = useCallback((img: HTMLImageElement|null) => {
    if (!img) return
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    offscreenCanvasRef.current = canvas
  }, [])

  const base = { coffee: 50, redbull: 300, monster: 1200, controller: 100, tablet: 200 }

  function price(basePrice: number, count: number, rate = 1.05) {
    return Math.max(1, Math.round(basePrice * Math.pow(rate, count)))
  }

  function controllerPrice(count: number) {
    const m = 1.0 + count * 0.1
    let rate = 1.05
    if (m >= 1 && m < 3) rate = 1.10
    else if (m >= 3 && m < 8) rate = 1.20
    else if (m >= 8) rate = 1.30
    return price(base.controller, count, rate)
  }

  const drinksAuto = coffeeCount * 1 + redbullCount * 5 + monsterCount * 20
  const autoSpeed = (postava.ma_autoclicker ? postava.autoclicker_cookies_za_sec ?? 0 : 0) + drinksAuto
  const cookiesPerSecond = Math.round(autoSpeed > 0 ? autoSpeed : 0)
  const cookiesPerClick = postava.cookies_za_klik_start + controllerCount

  useEffect(() => {
    const tick = setInterval(() => {
      if (gameOverRef.current) return
      setTime(t => t + 1)
      setCookies(c => c + cookiesPerSecond)
      setTotalCollected(s => s + cookiesPerSecond)
      setLogicalCaffeine(k => {
        if (Date.now() - lastClickRef.current < 1000) return k
        return Math.max(0, +(k - caffeineDecayPerSec).toFixed(2))
      })
    }, 1000)
    return () => clearInterval(tick)
  }, [cookiesPerSecond])

  useEffect(() => {
    let raf: number
    const update = () => {
      setDisplayCaffeine(prev => {
        const diff = logicalCaffeine - prev
        if (Math.abs(diff) < 0.1) return logicalCaffeine
        return +(prev + diff * 0.12).toFixed(2)
      })
      raf = requestAnimationFrame(update)
    }
    raf = requestAnimationFrame(update)
    return () => cancelAnimationFrame(raf)
  }, [logicalCaffeine])

  useEffect(() => {
    if (!unlockedRed && logicalCaffeine >= 200) setUnlockedRed(true)
    if (!unlockedMonster && logicalCaffeine >= 800) setUnlockedMonster(true)
    if (logicalCaffeine >= caffeineLimit && !gameOverRef.current) {
      gameOverRef.current = true; saveScore(); setGameOverReason("overdose"); setGameOver(true);
    }
    if (hasStarted && logicalCaffeine <= 0 && !gameOverRef.current) {
      gameOverRef.current = true; saveScore(); setGameOverReason("sleep"); setGameOver(true);
    }
  }, [logicalCaffeine, caffeineLimit, unlockedRed, unlockedMonster, hasStarted])

  useEffect(() => {
    if (caffeineMaskRef.current) {
      const pct = Math.min(100, (displayCaffeine / caffeineLimit) * 100)
      caffeineMaskRef.current.style.width = `${100 - pct}%`
    }
  }, [displayCaffeine, caffeineLimit])

  function buyCoffee() {
    const p = price(base.coffee, coffeeCount); if (cookies < p) return
    setCookies(c => c - p); setCoffeeCount(n => n + 1)
    setLogicalCaffeine(k => k + 60); setHasStarted(true); lastClickRef.current = Date.now()
  }
  function buyRedbull() {
    if (!unlockedRed) return
    const p = price(base.redbull, redbullCount); if (cookies < p) return
    setCookies(c => c - p); setRedbullCount(n => n + 1)
    setLogicalCaffeine(k => k + 80); setHasStarted(true); lastClickRef.current = Date.now()
  }
  function buyMonster() {
    if (!unlockedMonster) return
    const p = price(base.monster, monsterCount); if (cookies < p) return
    setCookies(c => c - p); setMonsterCount(n => n + 1)
    setLogicalCaffeine(k => k + 160); setHasStarted(true); lastClickRef.current = Date.now()
  }
  function buyController() {
    const p = controllerPrice(controllerCount); if (cookies < p) return
    setCookies(c => c - p); setControllerCount(n => n + 1)
  }
  function buyTablet() {
    const p = price(base.tablet, tabletCount); if (cookies < p) return
    setCookies(c => c - p); setTabletCount(n => n + 1)
    setLogicalCaffeine(k => +(k * 0.9).toFixed(2)); lastClickRef.current = Date.now()
  }

  function handleImageClick(e: React.MouseEvent<HTMLImageElement>) {
    if (gameOverRef.current) return
    const img = imgRef.current; const canvas = offscreenCanvasRef.current
    if (!img || !canvas) return
    const rect = img.getBoundingClientRect()
    const x = Math.floor((e.clientX - rect.left) * img.naturalWidth / rect.width)
    const y = Math.floor((e.clientY - rect.top) * img.naturalHeight / rect.height)
    try {
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      const d = ctx.getImageData(x, y, 1, 1).data
      if (d[3]! > 10) { hit() }
    } catch { hit() }
    function hit() {
      setBump(true); setTimeout(() => setBump(false), 180)
      setCookies(c => c + cookiesPerClick); setTotalCollected(s => s + cookiesPerClick)
      lastClickRef.current = Date.now()
    }
  }

  function saveScore() {
    try {
      const n = jmeno || localStorage.getItem("jmeno") || "Hráč"
      const s = JSON.parse(localStorage.getItem("zebricek") || "[]")
      s.push({ jmeno: n, cookies: Math.round(totalCollected), postava: postava.jmeno, datum: new Date().toISOString() })
      localStorage.setItem("zebricek", JSON.stringify(s))
    } catch {}
  }

  function formatTime(s: number) {
    if (s >= 3600) { const h = Math.floor(s/3600), m = Math.floor((s%3600)/60), sec = s%60; return `${h}h ${m}min ${sec}s` }
    return `${Math.floor(s/60)}m ${s%60}s`
  }

  function restart() {
    gameOverRef.current = false
    setCookies(0); setLogicalCaffeine(0); setDisplayCaffeine(0); setCoffeeCount(0); setRedbullCount(0)
    setMonsterCount(0); setControllerCount(0); setTabletCount(0)
    setTime(0); setTotalCollected(0); setGameOver(false); setHasStarted(false); setGameOverReason(null)
    setUnlockedRed(false); setUnlockedMonster(false)
  }

  let storedScores: any[] = []
  try { storedScores = JSON.parse(localStorage.getItem("zebricek") || "[]") } catch {}
  storedScores.sort((a, b) => b.cookies - a.cookies)

  const overdoseProverbs = ["Příliš mnoho kofeinu kazí radost z klikání.", "Když přeháníš s energetickými nápoji, zhasínáš samu sebe.", "Umírněnost přináší nejvíce sušenek.", "Kofeinový limit překročen, cesta ke dnu.", "Tvrdé probuzení z kofeinového snu.", "I ten největší klikač potřebuje míru.", "Srdce buší, ruce se třesou, konec hry.", "Hra je u konce, kofein zvítězil.", "Přecenil jsi své kofeinové síly.", "Závrať z nadměrného povzbuzení."]
  const sleepProverbs = ["Bez kofeinu jsi usnul na klávesnici.", "Tvoje tělo řeklo dost. Spánek je důležitý.", "Příště si dej aspoň jednu kávu!", "Kofeinové sny se nekonají, když spíš.", "Ztracen v hlubinách spánku, pryč od sušenek.", "Ať se ti zdají sladké sny o kávě.", "Když kofein dojde, oči se zavřou.", "I hrdinové potřebují odpočinek.", "Tvůj mozek potřeboval pauzu.", "Svět sušenek počká, až se vyspíš."]
  const proverb = React.useMemo(() => {
    if (gameOverReason === "sleep") return sleepProverbs[Math.floor(Math.random() * sleepProverbs.length)]
    else if (gameOverReason === "overdose") return overdoseProverbs[Math.floor(Math.random() * overdoseProverbs.length)]
    return ""
  }, [gameOverReason])

  const PriceTag = ({ val }: { val: number }) => (
    <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
      {val}
      <img src={favicon} style={{ width:28, height:28, verticalAlign:"middle", objectFit:"contain" }} alt="" />
    </span>
  )

  const fotoSrc = postava.jmeno.includes('Josef') ? josefFoto
    : postava.jmeno.includes('Karel') ? karelFoto
    : postava.jmeno.includes('Fanda') ? fandaFoto
    : postava.jmeno.includes('Závis') ? zavislakFoto
    : postava.jmeno.includes('Zbyš') ? zbysekFoto
    : nerdFoto
  const bgPosition = "center -210px"

  return (
    <div className="hra game-root">
      <div aria-hidden style={{ position: "fixed", inset: 0, zIndex: 0.5, pointerEvents: "none", backgroundImage: `url(${bgMenu})`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat", opacity: 0.1 }} />

      <div className="hamburger-container">
        <button className="hamburger-button" onClick={() => setMenuOpen(s => !s)}>☰</button>
        {menuOpen && (
          <div className="hamburger-menu">
            <button className="pixel-button" onClick={() => { setMenuOpen(false); onZpet() }}>Zpět do menu</button>
            <button className="pixel-button" onClick={() => { setMenuOpen(false); restart() }}>Restartovat hru</button>
          </div>
        )}
      </div>

      <div className="top-bar">
        <div className="hud-left panel-box">
          <div className="cookies-large">
            <div style={{ display:"flex", flexDirection:"column" }}>
              <div className="cookies-text">SUŠENKY</div>
              <div className="cookies-number-row">
                <div className="cookies-number">{cookies}</div>
                <img src={favicon} alt="cookie" className="hud-icon-inline" style={{ marginLeft: -10, marginTop: 3}} />
              </div>
            </div>
          </div>
        </div>
        <div className="time-display panel-box">Čas: {formatTime(time)}</div>
        <div className="hud-right panel-box">
          <div className="caffeine-inline">
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <img src={kofeinIkonka} alt="kofein" style={{ width: 72, height: 72, objectFit: "contain", marginRight: -15 }} />
              <div>KOFEIN</div>
              <div className="caffeine-number">{Math.round(displayCaffeine)}mg / {caffeineLimit}mg</div>
            </div>
          </div>
          <div className="caffeine-bar">
            <div className="caffeine-rainbow" />
            <div className="caffeine-mask" ref={caffeineMaskRef} />
          </div>
        </div>
      </div>

      <div className="game-area" style={{ position:"relative" }}>
        <div className="game-bg-layer" style={{ backgroundImage: `url(${postava.pozadi})`, backgroundPosition: bgPosition }} aria-hidden />

        <div className="left-panel" style={{ position:"relative", zIndex:10 }}>
          <div className="panel-box">
            <h4>Žebříček</h4>
            <div className="zebricek-wrap">
              <table className="zebricek-table">
                <thead><tr><th>#</th><th>Jméno</th><th>Postava</th><th>Sušenky</th></tr></thead>
                <tbody>
                  <tr className="zebricek-row-current">
                    <td>-</td><td>{jmeno || localStorage.getItem("jmeno") || "Hráč"}</td>
                    <td>{postava.jmeno}</td><td>{totalCollected}</td>
                  </tr>
                  {storedScores.slice(0, 5).map((s, i) => (
                    <tr key={i}><td>{i+1}</td><td>{s.jmeno}</td><td>{s.postava||''}</td><td>{s.cookies}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="character" style={{ position:"relative", zIndex:15 }}>
          <div className="character-frame">
            <img 
              ref={imgRef} 
              onLoad={ev => onImageLoad(ev.currentTarget)} 
              onClick={handleImageClick}
              src={fotoSrc} 
              alt={postava.jmeno}
              className={`big-character ${bump ? "bump" : ""}`} 
              draggable="false" 
              style={{
                WebkitUserDrag: "none",
                userSelect: "none",
                WebkitUserSelect: "none",
                msUserSelect: "none"
              }}
              onDragStart={(e) => e.preventDefault()} 
            />
          </div>
        </div>

        <div className="right-panel" style={{ position:"relative", zIndex:10 }}>
          <div className="panel-box">
            <h4>{postava.jmeno}</h4>
            <div className="slogan">{postava.slogan}</div>
            <ul className="vlastnosti">
              <li>Kofein limit: {postava.kofein_limit}mg</li>
              <li>Cookies/klik: {cookiesPerClick}</li>
              <li>Autoclicker: {autoSpeed > 0 ? `ano (${autoSpeed}/s)` : 'ne'}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="shop-under">
        <div className="shop">
          <div className="shop-main-row">
            <div className="shop-item coffee" onClick={buyCoffee}>
              <img src={kavaIcon} alt="kava" className={`shop-item-icon ${cookies < price(base.coffee, coffeeCount) ? "too-expensive" : ""}`} />
              <strong>KÁVA</strong>
              <div className="small">+60mg • +1 cps</div>
              <div className="shop-item-footer"><div className="shop-item-price"><PriceTag val={price(base.coffee, coffeeCount)} /></div></div>
            </div>
            <div className={`shop-item redbull ${!unlockedRed ? "locked" : ""}`} onClick={buyRedbull}>
              <img src={redbullIcon} alt="redbull" className={`shop-item-icon shop-item-icon-small ${cookies < price(base.redbull, redbullCount) ? "too-expensive" : ""}`} />
              <strong>REDBULL</strong>
              <div className="small">+80mg • +5 cps</div>
              {unlockedRed ? <div className="shop-item-footer"><div className="shop-item-price"><PriceTag val={price(base.redbull, redbullCount)} /></div></div> : <div className="shop-item-footer"><img src={lockIcon} alt="Locked" style={{ width:24, height:24, objectFit:"contain" }} /><span className="locked-text">200mg</span></div>}
            </div>
            <div className={`shop-item monster ${!unlockedMonster ? "locked" : ""}`} onClick={buyMonster}>
              <img src={monsterIcon} alt="monster" className={`shop-item-icon shop-item-icon-small ${cookies < price(base.monster, monsterCount) ? "too-expensive" : ""}`} />
              <strong>MONSTER</strong>
              <div className="small">+160mg • +20 cps</div>
              {unlockedMonster ? <div className="shop-item-footer"><div className="shop-item-price"><PriceTag val={price(base.monster, monsterCount)} /></div></div> : <div className="shop-item-footer"><img src={lockIcon} alt="Locked" style={{ width:24, height:24, objectFit:"contain" }} /><span className="locked-text">800mg</span></div>}
            </div>
            <div className="shop-item controller" onClick={buyController}>
              <img src={kontrolerIcon} alt="controller" className={`shop-item-icon ${cookies < controllerPrice(controllerCount) ? "too-expensive" : ""}`} />
              <strong>KONTROLER</strong>
              <div className="small">+{controllerCount} cps</div>
              <div className="shop-item-footer"><div className="shop-item-price"><PriceTag val={controllerPrice(controllerCount)} /></div></div>
            </div>
            <div className="shop-item antikofein" onClick={buyTablet}>
              <img src={antiIcon} alt="antikofein" className={`shop-item-icon ${cookies < price(base.tablet, tabletCount) ? "too-expensive" : ""}`} />
              <strong>ANTIKOFEIN</strong>
              <div className="small">-10% kofeinu</div>
              <div className="shop-item-footer"><div className="shop-item-price"><PriceTag val={price(base.tablet, tabletCount)} /></div></div>
            </div>
          </div>
        </div>
      </div>

      {gameOver && (
        <div className="game-over">
          <div className="game-over-box">
            <h2>{gameOverReason === "sleep" ? "USNUL JSI! 😴" : "GAME OVER ☠️"}</h2>
            <p className="small">{proverb}</p>
            <div className="cookies-display">Sušenky: {totalCollected}</div>
            <div className="small">Kofein: {Math.round(logicalCaffeine)}mg</div>
            <div className="small">Čas: {formatTime(time)}</div>
            <div style={{ display:'flex', gap:10, marginTop:12, justifyContent:'center' }}>
              <button className="pixel-button" onClick={restart}>Hrát znovu</button>
              <button className="pixel-button" onClick={() => { setGameOver(false); onZpet() }}>Menu</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export function APITester() { return null }
export default Hra